import { useEffect } from "react";
import { motion } from "framer-motion";
import { Home as HomeIcon, Flame, Footprints, Utensils, Sparkles } from "lucide-react";
import { IMAGES } from "@/context/AppContext";
import { NotifyForm } from "@/components/NotifyForm";
import { useNoIndex } from "@/hooks/useNoIndex";

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-70px" }} transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}>
    {children}
  </motion.div>
);

const RHYTHM = [
  { icon: HomeIcon, time: "Morning", title: "Gather", body: "You wake up under one roof with a handful of people who came for the same reason you did. Coffee. Scripture. Honesty. The day starts around a table, not a stage." },
  { icon: Flame, time: "The assignment", title: "Receive your Go & Do", body: "Every day comes with a real challenge — something that stretches faith into action. No hiding in the back row. Today, you do the thing you've only ever talked about." },
  { icon: Footprints, time: "Out the door", title: "Go", body: "Into neighborhoods, businesses, communities, real conversations, real people, real situations. This is where belief leaves your head and lands in your hands and feet." },
  { icon: Utensils, time: "Evening", title: "Return to the table", body: "You come home and eat together. You tell the stories — the awkward ones, the holy ones, the ones you can't believe just happened. You reflect on what God did in you and through you." },
  { icon: Sparkles, time: "By the last night", title: "Become", body: "Somewhere in the middle of it, the challenge stops feeling like a challenge. It starts becoming the way you live. You go home different — because you actually did it." },
];

const FOR = [
  "People who are done just talking about the life they want.",
  "People craving a few days that actually change how they live — not just how they feel.",
  "Christians ready to practice the way of Jesus in the real world.",
  "The curious who want to experience it before they explain it.",
  "Anyone willing to get a little uncomfortable for something that lasts.",
];

export default function OutpouredHousePage() {
  useNoIndex();
  useEffect(() => { document.title = "The Outpoured House · Outpoured"; window.scrollTo(0, 0); }, []);
  return (
    <div data-testid="outpoured-house-page" className="bg-[#F4EFE6] text-[#2A2825]">
      {/* HERO */}
      <section className="relative min-h-[80vh] flex items-end grain overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.retreat} alt="The Outpoured House gathered around a table" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#211E1B] via-[#211E1B]/65 to-[#211E1B]/25" />
        </div>
        <div className="relative z-10 w-full max-w-6xl mx-auto px-6 md:px-12 pb-16 md:pb-24 pt-28">
          <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="tracking-[0.3em] uppercase text-[10px] md:text-xs text-[#E8B486] mb-5">A multi-day immersive experience</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.85, delay: 0.28 }}
            className="font-serif-display font-light text-[#F4EFE6] text-4xl sm:text-6xl leading-[1.05] max-w-3xl">
            Come live the life you keep <span className="italic text-[#E8B486]">saying you want.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.85, delay: 0.42 }}
            className="mt-6 text-[#D9CFC2] font-body text-lg md:text-xl max-w-2xl leading-relaxed">
            A few days under one roof where faith stops being a conversation and becomes something you actually do.
          </motion.p>
        </div>
      </section>

      {/* MANIFESTO — NOT A RETREAT */}
      <section className="py-20 md:py-32 bg-[#2A2825] text-[#F4EFE6] grain relative">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal><p className="tracking-overline text-xs uppercase text-[#C79A6E] mb-6">Read this first</p></Reveal>
          <Reveal delay={0.06}><h2 className="font-serif-display font-light text-3xl md:text-5xl leading-tight mb-10">The Outpoured House is not a retreat from your life.</h2></Reveal>
          <Reveal delay={0.12}>
            <div className="space-y-6 text-[#D9CFC2] leading-relaxed font-body text-lg max-w-2xl mx-auto">
              <p>You aren't coming to hide in a pretty house for seven days while somebody talks at you.</p>
              <p className="font-serif-display italic text-2xl md:text-3xl text-[#E8B486] leading-snug">You live together. Eat together. Gather. Receive the day's challenge. Then go.</p>
              <p>Into neighborhoods, businesses, communities, real conversations, real people and real situations. And then you come home, tell the stories, and reflect on what just happened. You don't leave with more notes. You leave with a life you actually practiced.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* THE RHYTHM OF A DAY */}
      <section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="tracking-overline text-xs uppercase text-[#A9724E] mb-4">The rhythm of a day</p>
            <h2 className="font-serif-display font-light text-3xl md:text-5xl leading-tight mb-14">Gather. Go. Come home changed.</h2>
          </Reveal>
          <div className="space-y-2">
            {RHYTHM.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.06}>
                <div className="flex gap-6 md:gap-10 py-8 border-b border-[#D8CEBE]">
                  <s.icon className="w-7 h-7 text-[#A9724E] shrink-0 mt-1" strokeWidth={1.25} />
                  <div>
                    <p className="tracking-overline text-[10px] uppercase text-[#8a7a6a] mb-1">{s.time}</p>
                    <h3 className="font-serif-display text-2xl md:text-3xl text-[#2A2825] mb-2">{s.title}</h3>
                    <p className="text-[#5E4B3C] leading-relaxed font-body">{s.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="py-20 md:py-28 bg-[#EAE3D9]/50 border-y border-[#D8CEBE]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Reveal><p className="tracking-overline text-xs uppercase text-[#A9724E] mb-8 text-center">Who the House is for</p></Reveal>
          <ul className="space-y-4">
            {FOR.map((t, i) => (
              <Reveal key={t} delay={i * 0.06}>
                <li className="flex items-start gap-3 font-serif-display text-xl md:text-2xl font-light text-[#2A2825] leading-snug"><span className="text-[#A9724E] mt-1">✦</span>{t}</li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#2A2825] text-[#F4EFE6] relative grain">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <p className="tracking-overline text-xs uppercase text-[#E8B486] mb-6">The next House</p>
            <h2 className="font-serif-display font-light text-3xl md:text-5xl leading-tight mb-6">There's a seat at this table with your name on it.</h2>
            <p className="text-[#C9BEB0] font-body leading-relaxed max-w-lg mx-auto mb-10">Spots are limited and intentional. Leave your email and you'll be the first to know when the next Outpoured House opens.</p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="flex justify-center">
              <div className="bg-[#F4EFE6] text-[#2A2825] p-8 md:p-10 w-full max-w-md rounded-2xl">
                <p className="text-xs tracking-overline uppercase text-[#8a7a6a] mb-3 text-left">Claim your spot on the list</p>
                <NotifyForm track="outpoured-house" testid="notify-house-page" buttonText="Keep me first in line" successText="You're on the list — we'll reach out the moment the next House opens. ✦" placeholder="Your email address" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
